import './scss/main.scss'
import api from 'api/api'
import Token from 'api/auth-token-wrapper'
import { CookieBanner, Footer, Nav, Toast } from 'components'
import Loader from 'components/Loader'
import 'i18n'
import loadLoggedUser from 'mixins/loadLoggedUser'
import React, { useEffect, useState } from 'react'
import 'react-dates/initialize' // initialize the date-picker styles
import TagManager from 'react-gtm-module'
import { useSelector } from 'react-redux'
import { useNavigate, useLocation } from 'react-router-dom'
import { useAuthReady } from '@envision/ev-ui-component-library'
import Routes from './routes.jsx'
import { routePaths } from './route_paths'
import { store } from './store'
import configurationActions from 'store/configuration'
import loaderActions from 'store/loader'
import userActions, { setRegistrationComplete } from 'store/user'
import { envConsole } from 'utils/console'
import { concealMessageDeterministic } from 'utils/encrypt'
import Constants from './constants'
import { IMAGES } from './constants/images'
import { IRootState } from './store'
import { ILoggedUser } from './typeScript/interfaces/IUser'
import { configureSentry } from './Sentry'
import { getConfig } from './config'

configureSentry()

const authWall = [
  routePaths.portalOverview,
  routePaths.portalCharger,
  routePaths.portalDocuments,
  routePaths.portalPhotoUpload,
  routePaths.portalPayment,
  routePaths.portalProfile,
  routePaths.portalSupport,
  routePaths.portalTariff,
  routePaths.portalPayout,
  routePaths.portalFleetData,
  routePaths.portalCards,
]

function App() {
  const authReady = useAuthReady()
  const { dispatch } = store
  const location = useLocation()
  const navigate = useNavigate()
  const [gtmInit, setGtmInit] = useState(false)
  const [, setOpportunityFetchComplete] = useState(false)
  const user = useSelector<IRootState, ILoggedUser | null>(
    (state) => state.user.user
  )
  const isLogged = useSelector<IRootState, boolean>(
    (state) => state.user.isLogged
  )
  const userLoading = useSelector<IRootState, boolean>(
    (state) => state.user.userLoading
  )
  const isMarketingCookie = useSelector<IRootState, boolean>(
    (state) => state.cookie.isMarketingCookie
  )

  useEffect(() => {
    if (!authReady) return

    if (isLogged) {
      api.user.getAccount().then((response) => {
        dispatch(userActions.setUserData(response.data))
      })
    }
  }, [authReady, isLogged])

  // Portal routes on authWall never hide the loader via cacheImages; many pages
  // (e.g. Payout, Profile, Tariff) never call setShowLoader(false) either.
  useEffect(() => {
    if (!authReady || userLoading) {
      return
    }
    dispatch(loaderActions.setShowLoader(false))
  }, [authReady, userLoading, dispatch])

  const redirectOnNotLoggedIn = async () => {
    dispatch(userActions.setAutoUserLoginTriggered(false))
    const pageName = location.pathname

    if (Token.getSSoToken()) {
      return
    }

    if (!authWall.includes(pageName)) {
      return
    }

    const config = await getConfig()
    if (config.universLoginPage) {
      window.location.replace(config.universLoginPage)
    }
  }

  const loadAndCacheAssets = async () => {
    const imageAssets = IMAGES
    cacheImages(imageAssets)
  }

  const cacheImages = async (images) => {
    const promises = await images.map((image) => {
      return new Promise((resolve, reject) => {
        const loadImg = new Image()
        loadImg.src = image
        loadImg.onload = () => resolve(image)
        loadImg.onerror = (err) => reject(err)
      })
    })

    await Promise.all([...promises])
      .then(() => {
        if (!authWall.includes(location.pathname)) {
          dispatch(loaderActions.setShowLoader(false))
        }
      })
      .catch(() => {
        if (!authWall.includes(location.pathname)) {
          dispatch(loaderActions.setShowLoader(false))
        }
      })
  }

  useEffect(() => {
    if (!authReady) return

    loadAndCacheAssets()

    const ssoBootstrapped = Token.bootstrapFromSSOIfAvailable()
    const activeToken = Token.getToken() || ssoBootstrapped?.access_token

    if (activeToken) {
      const successCallback = () => {
        dispatch(userActions.setAutoUserLoginTriggered(true))
      }
      loadLoggedUser(dispatch, successCallback, redirectOnNotLoggedIn)
      return
    }

    redirectOnNotLoggedIn()
  }, [authReady])

  useEffect(() => {
    if (
      isLogged &&
      Token.getToken() &&
      (user?.registrationComplete === null ||
        user?.registrationComplete === undefined)
    ) {
      api.leads
        .getLeadStatus()
        .then((response) => {
          setOpportunityFetchComplete(true)
          if (response.data?.stageName?.toLowerCase() === 'new') {
            dispatch(setRegistrationComplete(false))
          } else {
            dispatch(setRegistrationComplete(true))
          }
        })
        .catch(() => {
          setOpportunityFetchComplete(true)
          dispatch(setRegistrationComplete(true))
        })
    } else if (!isLogged) {
      setOpportunityFetchComplete(true)
    }
  }, [user?.registrationComplete, isLogged])

  useEffect(() => {
    // Wait until user profile is loaded — on reload `user` is briefly null/undefined
    // and `!user?.isReimbursementActivated` would incorrectly redirect to `/`.
    if (!isLogged || userLoading || !user) {
      return
    }

    if (
      user.canUpdatePaymentInformation === false &&
      location.pathname === routePaths.portalPayment
    ) {
      navigate(routePaths.portalOverview)
    }
    if (
      user.isReimbursementActivated === false &&
      location.pathname === routePaths.portalPayout
    ) {
      navigate(routePaths.portalOverview)
    }
    if (
      user.isFleetDataEnrichment === false &&
      location.pathname === routePaths.portalFleetData
    ) {
      navigate(routePaths.portalOverview)
    }

    if (
      !isLogged &&
      authWall.includes(location.pathname) &&
      !userLoading &&
      !Token.getSSoToken()
    ) {
      redirectOnNotLoggedIn()
    }
    user?.type && envConsole('USERTYPE: ' + user?.type)
  }, [isLogged, userLoading, location.pathname, user, navigate])

  useEffect(() => {
    isMarketingCookie && initGtm()
    checkAndCleanUpLocalStorageForCurrentVersion()
  }, [isMarketingCookie])

  const initGtm = () => {
    if (!gtmInit) {
      const tagManagerArgs = {
        gtmId: Constants.GTM_ID,
      }

      TagManager.initialize(tagManagerArgs)

      setGtmInit(true)
    }
  }

  const checkAndCleanUpLocalStorageForCurrentVersion = () => {
    const key = `version_${concealMessageDeterministic('cache_key').toString()}`
    const encodedCurrentVal = localStorage.getItem(key)
    const encodedNewVal = concealMessageDeterministic(
      Constants.VERSION
    ).toString()
    if (!encodedCurrentVal || encodedCurrentVal !== encodedNewVal) {
      localStorage.removeItem(key)
      Token.removeLocalStorageTokensOnly()
      dispatch(userActions.resetUser())
      dispatch(configurationActions.resetConfigurationData())
      dispatch(configurationActions.resetInstallationAnswersSelection())
      localStorage.setItem(key, encodedNewVal)
    }
  }

  if (!authReady) {
    return <Loader />
  }

  return (
    <>
      <Loader />
      <Nav />

      <Routes />
      <Footer />
      <Toast />
      <CookieBanner />
    </>
  )
}

export default App
